import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";

// Simple token guard for MVP
// Precedence of expected token:
// 1) process.env.REACT_APP_ADMIN_TOKEN (build-time)
// 2) localStorage.adminExpectedToken (runtime setup)
// If neither exists, user can create a token the first time (stored in localStorage)

const normalize = (s) => (s || "").replace(/^['"]|['"]$/g, "").trim();

export default function AdminGuard({ children }) {
  const { toast } = useToast();
  const nav = useNavigate();
  const loc = useLocation();

  const expectedEnv = normalize(process.env.REACT_APP_ADMIN_TOKEN || "");
  const [expected, setExpected] = useState(
    expectedEnv || normalize(localStorage.getItem("adminExpectedToken") || "")
  );
  const [input, setInput] = useState("");
  const hasToken = !!localStorage.getItem("adminToken");

  // Handle logout via ?logout=1 and auto-unlock via ?key=<token>
  useEffect(() => {
    const qp = new URLSearchParams(loc.search);
    if (qp.get("logout") === "1") {
      localStorage.removeItem("adminToken");
      toast({ title: "Signed out" });
      nav("/", { replace: true });
      return;
    }
    const key = normalize(qp.get("key") || "");
    if (key && (key === normalize(expected) || key === normalize(expectedEnv))) {
      localStorage.setItem("adminToken", "ok");
      toast({ title: "Access granted" });
      qp.delete("key");
      const path = loc.pathname + (qp.toString() ? `?${qp.toString()}` : "");
      nav(path || "/admin", { replace: true });
    }
  }, [loc.search, expected]);

  const handleSetup = (e) => {
    e.preventDefault();
    if (expectedEnv) return; // cannot override env-based secret
    if (!input.trim()) return toast({ title: "Enter a token" });
    const val = normalize(input);
    localStorage.setItem("adminExpectedToken", val);
    setExpected(val);
    setInput("");
    toast({ title: "Admin token set" });
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (!expected) return toast({ title: "Token not configured" });
    if (normalize(input) === normalize(expected)) {
      localStorage.setItem("adminToken", "ok");
      setInput("");
      toast({ title: "Access granted" });
      // stay on page; AdminProjects renders underneath
    } else {
      toast({ title: "Invalid token" });
    }
  };

  if (!expected) {
    return (
      <div className="mx-auto max-w-md px-4 py-16">
        <Card>
          <CardHeader><CardTitle>Set Admin Token (one-time)</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-3">Create a token to protect the admin page. Keep it private. You can change it later by clearing your browser storage.</p>
            <form onSubmit={handleSetup} className="space-y-3">
              <Input placeholder="Enter new admin token" value={input} onChange={(e) => setInput(e.target.value)} />
              <Button type="submit" className="bg-sky-600 text-white">Set Token</Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!hasToken) {
    return (
      <div className="mx-auto max-w-md px-4 py-16">
        <Card>
          <CardHeader><CardTitle>Admin Access</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-3">
              <Input placeholder="Enter admin token" value={input} onChange={(e) => setInput(e.target.value)} />
              <div className="flex gap-2">
                <Button type="submit" className="bg-sky-600 text-white">Unlock</Button>
                <Button type="button" variant="outline" onClick={() => nav("/", { replace: true })}>Cancel</Button>
              </div>
              {expectedEnv && <p className="text-xs text-slate-500">Token set via environment. Contact developer to rotate.</p>}
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div>
      {children}
    </div>
  );
}